import { Link } from "react-router-dom";
import Footer from "@/components/landing/Footer";
import { HOMEIQ_LOGO } from "@/assets/brand";

// Shared shell for /privacy, /terms, /disclaimer.
// Kept simple on purpose — these pages exist to be read, not clicked around.
// The nav shows the HomeIQ mark + a single "Back to home" link. Everything
// else about the page belongs to the content the child component renders.
export default function LegalLayout({ eyebrow, title, lede, lastUpdated, children }) {
    return (
        <div className="min-h-screen bg-white text-[#0A1F44] font-body flex flex-col">
            <nav className="border-b border-[#0A1F44]/8 bg-white/90 backdrop-blur-xl sticky top-0 z-30">
                <div className="max-w-4xl mx-auto flex items-center justify-between px-6 md:px-10 h-14 md:h-16">
                    <Link to="/" data-testid="legal-nav-home" className="flex items-center gap-2">
                        <img src={HOMEIQ_LOGO} alt="HomeIQ" className="h-9 md:h-10 w-auto object-contain" />
                    </Link>
                    <Link
                        to="/"
                        data-testid="legal-nav-back"
                        className="text-xs font-semibold text-[#0A1F44]/70 hover:text-[#1E6BFF] transition-colors"
                    >
                        ← Back to home
                    </Link>
                </div>
            </nav>

            <main className="flex-1 max-w-3xl w-full mx-auto px-6 md:px-10 py-16 md:py-24">
                <header className="mb-12 md:mb-16">
                    <p className="text-[11px] tracking-[0.3em] uppercase text-[#1E6BFF] font-semibold mb-4">— {eyebrow}</p>
                    <h1 className="font-display font-extrabold text-4xl sm:text-5xl leading-[1.05] tracking-tight">
                        {title}
                    </h1>
                    {lede && (
                        <p className="mt-6 text-[#0A1F44]/70 text-lg leading-relaxed max-w-2xl">
                            {lede}
                        </p>
                    )}
                </header>

                <article className="legal-content space-y-10 text-[15px] leading-[1.75] text-[#0A1F44]/85">
                    {children}
                </article>

                {lastUpdated && (
                    <p className="mt-16 pt-8 border-t border-[#0A1F44]/10 text-xs text-[#0A1F44]/50 tracking-wide">
                        Last updated · {lastUpdated}
                    </p>
                )}
            </main>

            <Footer />
        </div>
    );
}

// Shared building blocks used by all three legal pages — keeps typography
// consistent without repeating Tailwind classes across every heading/list.
export function Section({ title, children }) {
    return (
        <section>
            <h2 className="font-display font-extrabold text-xl md:text-2xl text-[#0A1F44] mb-4 tracking-tight">
                {title}
            </h2>
            <div className="space-y-4">{children}</div>
        </section>
    );
}

export function List({ items }) {
    return (
        <ul className="space-y-2.5 list-none">
            {items.map((item, i) => (
                <li key={i} className="pl-5 relative">
                    <span aria-hidden className="absolute left-0 top-[0.65em] w-1.5 h-1.5 rounded-full bg-[#1E6BFF]" />
                    {item}
                </li>
            ))}
        </ul>
    );
}
